/* R148d · Feb 2026 — SearchableAssigneePicker
 * Combobox-style inline assignee dropdown used in task list rows.
 * Sorted alphabetically by name, searchable (name only — no email
 * shown), and keyboard-accessible. Renders a compact PersonChip when
 * the row has an assignee, or a subtle "unassigned" hint otherwise. */
import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { PersonChip } from "@/components/PersonChip";
import { Check, User } from "lucide-react";

export default function SearchableAssigneePicker({
  value,               // assignee_id or null
  members = [],
  memberName,          // (id) => name
  onChange,            // (newId | null) => void
  size = "sm",         // sm | md
  placeholder = "unassigned",
  testid,
}) {
  const [open, setOpen] = useState(false);

  // R148d · Sort alphabetically once; hide inactive members if the row
  // carries a `status` field.
  const sorted = useMemo(() => {
    const active = (members || []).filter((m) => m.status !== "removed");
    return [...active].sort((a, b) => {
      const an = (a.name || a.email || "").toLowerCase();
      const bn = (b.name || b.email || "").toLowerCase();
      return an.localeCompare(bn);
    });
  }, [members]);

  const height = size === "md" ? "h-8" : "h-6";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={`${height} text-xs border-0 bg-transparent hover:bg-secondary/40 px-1 rounded-none w-full text-left flex items-center gap-1 min-w-0`}
          data-testid={testid}
        >
          {value ? (
            <PersonChip userId={value} name={memberName?.(value) || ""} />
          ) : (
            <span className="text-muted-foreground italic text-xs flex items-center gap-1"><User className="w-3 h-3" />{placeholder}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0 rounded-none"
        onClick={(e) => e.stopPropagation()}
        onPointerDownOutside={() => setOpen(false)}
        align="start"
        data-testid={testid ? `${testid}-popover` : undefined}
      >
        <Command>
          <CommandInput placeholder="Search teammate…" className="h-9 text-xs" data-testid={testid ? `${testid}-search` : undefined} />
          <CommandList className="max-h-64">
            <CommandEmpty>No teammates found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__unassigned__"
                onSelect={() => { onChange(null); setOpen(false); }}
                data-testid={testid ? `${testid}-unassigned` : undefined}
              >
                <span className="italic text-muted-foreground">— Unassigned —</span>
                {!value && <Check className="ml-auto w-3.5 h-3.5" />}
              </CommandItem>
              {sorted.map((m) => {
                const id = m.user_id || m.id;
                const name = m.name || m.email || id;
                const selected = value === id;
                return (
                  <CommandItem
                    key={id}
                    value={name}
                    onSelect={() => { onChange(id); setOpen(false); }}
                    data-testid={testid ? `${testid}-opt-${id}` : undefined}
                  >
                    <span className="truncate">{name}</span>
                    {selected && <Check className="ml-auto w-3.5 h-3.5" />}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
